import React, {useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router';
import {Box, Container, Skeleton, Stack, styled, Typography, useMediaQuery, useTheme} from '@mui/material';
import {map} from 'nanostores';
import {useStore} from '@nanostores/react';
import withMainContainer from '../../components/WithMainContainer.js';
import {withPermGuard} from '../../components/PermGuard.js';
import CardContainer from '../../components/CardContainer.js';
import {usePosts, useTags} from './blog_util.js';

const $postsFilter = map({
    page: 1,
    pageSize: 50,
    tag: null,
    category: null,
});

const TagItemSkeleton = ({width})=><Skeleton variant="rounded" width={width} height={40}/>;

const BlogTagsSkeleton = ()=><>
  <TagItemSkeleton width={50}/>
  <TagItemSkeleton width={200}/>
  <TagItemSkeleton width={130}/>
</>;

const TagItemBox = styled(Box, {shouldForwardProp: p=>!p.startsWith('$')})`
    cursor: pointer;
    padding: 4px;
    border-radius: 8px;
    border: 2px solid ${p=>p.theme.palette.primary.main};
    ${p=>p.$selected ? `background-color: ${p.theme.palette.primary.light};` : ''}
    &:hover {
        border-color: ${p=>p.theme.palette.primary.light};
    }
`;

const TagItem = props=>{
    let {name, slug} = props;
    let filter = useStore($postsFilter);
    let onClick = useCallback(
        ()=>$postsFilter.setKey('tag', filter?.tag==slug ? null : slug),
        [filter?.tag, slug]);
    return <TagItemBox onClick={onClick} $selected={filter?.tag==slug}>
      {name}
    </TagItemBox>;
};

const BlogTags = ()=>{
    let [tags, loading] = useTags();
    return <Stack direction="row" flexWrap="wrap" gap={1}
      justifyContent="center" alignItems="center">
      {loading ? <BlogTagsSkeleton/> : tags?.map(tag=><TagItem key={tag.slug} {...tag}/>)}
    </Stack>;
};

const useBlogImageSize = ()=>{
    let theme = useTheme();
    let sm = useMediaQuery(theme.breakpoints.down('sm'));
    let md = useMediaQuery(theme.breakpoints.down('md'));
    return useMemo(()=>{
        if (sm)
            return [250, 200];
        if (md)
            return [300, 200];
        return [300, 200];
    }, [md, sm]);
};

const BlogItemSkeleton = ()=>{
    let [width, height] = useBlogImageSize();
    return <CardContainer>
      <Stack direction="column" gap={1} width={width}>
        <Skeleton variant="rounded" width={width} height={height}/>
        <Stack direction="column" gap={1} padding={2}>
          <Skeleton variant="rounded" width={120} height={40}/>
          <Skeleton variant="rounded" width={60} height={30}/>
          <Skeleton variant="rounded" width={100} height={30}/>
          <Skeleton variant="rounded" width={70} height={30}/>
        </Stack>
      </Stack>
    </CardContainer>;
};

const BlogItem = props=>{
    let {slug, title, summary, featured_image, featured_image_alt} = props;
    let [width, height] = useBlogImageSize();
    let navigate = useNavigate();
    return <CardContainer onClick={()=>navigate(`/blog/post/${slug}`)} sx={{
        cursor: 'pointer',
        ':hover': {transform: 'translateY(-4px)'},
        transition: 'transform 0.1s ease-in',
    }}>
      <Stack direction="column" gap={1} width={width}>
        {featured_image&&<Box component="img" width={width} height={height}
          src={featured_image} alt={featured_image_alt}
          sx={{objectFit: 'cover'}}/>}
        <Stack direction="column" gap={1} padding={2}>
          <Typography fontWeight="bold">
            {title}
          </Typography>
          <Typography>
            {summary}
          </Typography>
        </Stack>
      </Stack>
    </CardContainer>;
};

const BlogGridSkeleton = ()=><>
  <BlogItemSkeleton/>
  <BlogItemSkeleton/>
  <BlogItemSkeleton/>
</>;

const BlogGrid = ()=>{
    let filter = useStore($postsFilter);
    let [posts, loading] = usePosts(filter);
    console.log(posts);
    return <Stack direction="row" gap={2} flexWrap="wrap" justifyContent="center">
      {loading ? <BlogGridSkeleton/> : posts?.data.map(post=><BlogItem key={post.slug} {...post}/>)}
    </Stack>;
};

const Blog = ()=>{
    return <Container>
      <Stack direction="column" gap={3} justifyContent="center" alignItems="center">
        <Typography variant="h3">
          מאמרים
        </Typography>
        <BlogTags/>
        <BlogGrid/>
      </Stack>
    </Container>;
};

export default withMainContainer(withPermGuard(Blog, 'admin'), {footerMargin: 4});
